import { findKind, pointsNeeded, type DrawingKind } from './kinds.js';
import { normalizePose, type StickerPose } from './sticker-pose.js';
import { normalizeDrawingAlert, type DrawingAlert } from './drawing-alerts.js';
import { normalizeShapeOptions, type ShapeOptions } from './shape-options.js';
import { normalizeFibOptions, type FibOptions } from './fib.js';
import { normalizePositionOptions, type PositionOptions } from './position.js';
import { normalizeTextStyle, type TextStyle } from './text-style.js';
import { isVolumeKind, normalizeVolumeOptions, type VolumeOptions } from './volume-tools.js';
import { normalizeFiboTrade, type FiboTrade } from '@specter/types';

export type { DrawingKind };

/**
 * Разметка пользователя: линии, уровни, зоны, подписи.
 *
 * Точка хранится во времени и цене, а не в пикселях. Пиксель живёт до
 * первой прокрутки; время и цена — это то, что человек на самом деле
 * отметил, и оно переживёт и масштаб, и перезагрузку, и другое
 * устройство.
 *
 * Разметка принадлежит инструменту: уровень, проведённый по BTC, на ETH
 * не значит ничего. Поэтому в каждой фигуре записано, где она нарисована.
 */

export interface DrawingPoint {
  /** Время бара, unix ms. */
  readonly time: number;
  readonly price: number;
}

export interface Drawing {
  readonly id: string;
  readonly kind: DrawingKind;
  readonly venue: string;
  readonly symbol: string;
  /** Одна точка у уровня и подписи, две у остальных. */
  readonly points: readonly DrawingPoint[];
  readonly color: string;
  readonly width: number;
  /** Сплошная, пунктир или точки. */
  readonly style: LineStyle;
  /**
   * Замок на конкретную фигуру.
   *
   * Отдельно от общего замка панели: тот запирает всю разметку разом,
   * а этот — одну выверенную линию, которую не хочется сдвинуть, пока
   * рисуешь рядом остальное.
   */
  readonly locked?: boolean;
  /** Только для подписи. */
  readonly text?: string;
  /** Настройки уровней — только у инструментов Фибоначчи. */
  readonly fib?: FibOptions;
  /** Настройки счёта и риска — только у прогноза позиции. */
  readonly position?: PositionOptions;
  /** Оформление подписи — только у текстовых инструментов. */
  readonly textStyle?: TextStyle;
  /** Вид прямоугольника и регрессии: средняя линия, цвета канала. */
  readonly options?: ShapeOptions;
  /** Настройки VWAP с привязкой и профилей объёма. */
  readonly volume?: VolumeOptions;
  /** Оповещение, повешенное на рисунок: условие и сколько раз будить. */
  readonly alert?: DrawingAlert;
  /** Размер и поворот стикера — в пикселях экрана (см. sticker-pose.ts). */
  readonly pose?: StickerPose;
  /**
   * «Фибо-пульт»: бот торгует по этой сетке — только у «Коррекции
   * Фибоначчи». Хранится в фигуре, как оповещение: сервер читает сетку
   * из разметки и сам ставит и снимает заявки (fibo-pult.ts в types).
   */
  readonly trade?: FiboTrade;
}

export type LineStyle = 'solid' | 'dashed' | 'dotted';

export const LINE_STYLES: ReadonlyArray<{ id: LineStyle; name: string; dash: number[] }> = [
  { id: 'solid', name: 'Сплошная', dash: [] },
  { id: 'dashed', name: 'Пунктир', dash: [6, 4] },
  { id: 'dotted', name: 'Точки', dash: [1, 3] },
];

/** Толщины, между которыми есть смысл выбирать. */
export const LINE_WIDTHS: readonly number[] = [1, 2, 3, 4];

/**
 * Палитра разметки.
 *
 * Короткая и с понятными ролями: фирменный тон, рост, падение,
 * нейтральный, плюс два холодных для второстепенных построений.
 */
/**
 * Прежний оранжевый акцент.
 *
 * До перехода на фирменный спектр им рисовалось всё по умолчанию, так
 * что в сохранённых профилях он лежит не как осознанный выбор, а как
 * остаток старой темы. Поэтому при чтении он заменяется — иначе на
 * графике так и остался бы оранжевый, которого больше нигде нет.
 */
const LEGACY_DEFAULT_COLOR = '#E3A455';

export const DRAWING_COLORS: readonly string[] = [
  '#6E8BFF',
  '#3FBF87',
  '#F0616F',
  '#E7EAF0',
  '#5AC8D8',
  '#9B8CE0',
];

/**
 * Приведение сохранённой фигуры к текущей форме.
 *
 * Профиль живёт дольше кода: в нём может лежать разметка, сделанная
 * прошлой версией. Одна непонятная запись не должна стирать остальные —
 * поэтому здесь `null`, а не исключение.
 */
/** Цвет фигуры: свой, если выбран; фирменный — если его нет или он старый. */
function brandColor(value: unknown): string {
  if (typeof value !== 'string' || value === LEGACY_DEFAULT_COLOR) return DRAWING_COLORS[0]!;
  return value;
}

export function normalizeDrawing(value: unknown): Drawing | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Partial<Drawing>;

  if (typeof raw.id !== 'string' || typeof raw.kind !== 'string') return null;
  if (typeof raw.venue !== 'string' || typeof raw.symbol !== 'string') return null;
  if (!Array.isArray(raw.points) || raw.points.length === 0) return null;

  const points = raw.points
    .filter(
      (point): point is DrawingPoint =>
        typeof point === 'object' &&
        point !== null &&
        Number.isFinite((point as DrawingPoint).time) &&
        Number.isFinite((point as DrawingPoint).price),
    )
    .map((point) => ({ time: point.time, price: point.price }));

  // Вид, которого больше нет, восстанавливать нечем.
  if (!findKind(raw.kind)) return null;
  if (points.length < pointsNeeded(raw.kind as DrawingKind)) return null;

  return {
    id: raw.id,
    kind: raw.kind as DrawingKind,
    venue: raw.venue,
    symbol: raw.symbol,
    points,
    color: brandColor(raw.color),
    width: typeof raw.width === 'number' ? raw.width : 1,
    // Разметка прошлой версии стиля не знала — она была сплошной.
    style: isLineStyle(raw.style) ? raw.style : 'solid',
    ...(raw.locked === true ? { locked: true } : {}),
    ...(raw.fib ? { fib: normalizeFibOptions(raw.fib) } : {}),
    ...(raw.position ? { position: normalizePositionOptions(raw.position) } : {}),
    ...(raw.textStyle ? { textStyle: normalizeTextStyle(raw.textStyle) } : {}),
    ...(raw.options ? { options: normalizeShapeOptions(raw.options) } : {}),
    ...(raw.volume && isVolumeKind(raw.kind as DrawingKind) ? { volume: normalizeVolumeOptions(raw.kind as DrawingKind, raw.volume) } : {}),
    ...(raw.alert ? { alert: normalizeDrawingAlert(raw.alert) } : {}),
    ...(raw.pose && normalizePose(raw.pose) ? { pose: normalizePose(raw.pose) } : {}),
    ...(raw.kind === 'fib-retracement' && normalizeFiboTrade(raw.trade) ? { trade: normalizeFiboTrade(raw.trade)! } : {}),
    ...(typeof raw.text === 'string' ? { text: raw.text } : {}),
  };
}

function isLineStyle(value: unknown): value is LineStyle {
  return value === 'solid' || value === 'dashed' || value === 'dotted';
}
